import * as endpoints from "../../../lib/api/endpoints";
import classNames from "../../../lib/classnames";
import styles from "./InstantCardModal.module.css";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Modal from "../../Modal";
import {Focusable, ModalProps} from "../../Modal/Modal";
import Spinner from "../../Spinner";
import TextLink from "../../TextLink";
import {InstantCard} from "../../../lib/api/models";
import maskInput from "../../../lib/maskInput";
import {centsToDollarString} from "../../../lib/formatting";

const strings = {
    header: "Congratulations!",

    p1:
        "Here is your account information. You can use it now to make online purchases before you receive your card.",
    accountNumber: "Account Number:",
    expirationDate: "Expiration Date:",
    securityCode: "Security Code:",
    creditLimit: "Credit Limit (this is the amount you can spend using your credit card):",
    p2:
        "For security purposes, keep your account information in a secure and safe place. When you leave this page, you will no longer have access to this information.",
    p3a:
        "To access your account information online or through our mobile app, you need to register your account through our website (",
    website: "www.1fbusa.com",
    p3b: ") or mobile app (1FBUSA Mobile). You will be able to do this within 4-5 days.",
    p4:
        "If you have any questions, please call us at 1-800-733-1732, Monday through Friday between 8:00 a.m. and 8:00 p.m. (Central Time) or Saturday between 8:00 a.m. and 5:00 p.m. (Central Time) excluding Federal holidays.",

    unableToRetrieve: "Unable to retrieve your account information.",

    finish: "Finish",
};

const websiteLinkTo = "https://www.1fbusa.com";

const ACCOUNT_NUMBER_MASK = "9999 9999 9999 9999";

const CLOSE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

type Props<T> = Pick<ModalProps<T>, "isOpen" | "triggerElement"> & {
    onClose: () => void;
};

export default function InstantCardModal<T extends Focusable>({
    isOpen,
    onClose,
    triggerElement,
}: Props<T>) {
    const closeTimeoutRef = useRef<NodeJS.Timeout>();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [instantCard, setInstantCard] = useState<InstantCard>();

    const loadInstantCard = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await endpoints.getInstantCard();
            setInstantCard(data);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadInstantCard();
        }
    }, [loadInstantCard, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            return;
        }

        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(onClose, CLOSE_TIMEOUT_MS);

        const ref = closeTimeoutRef.current;
        return () => {
            if (ref) {
                clearTimeout(ref);
            }
        };
    }, [onClose, isOpen]);

    return (
        <Modal
            id="instantCardInformationModal"
            className={styles.modal}
            label={strings.header}
            isOpen={isOpen}
            onSubmit={onClose}
            onSubmitLabel={strings.finish}
            triggerElement={triggerElement}
        >
            {loading ? (
                <div className={styles.loadingContainer}>
                    <Spinner isVisible={true} className={styles.loading} />
                </div>
            ) : error || !instantCard ? (
                <div className={styles.pText}>
                    <div className={styles.p}>{strings.unableToRetrieve}</div>
                    <div className={styles.p}>{strings.p4}</div>
                </div>
            ) : (
                <div className={styles.pText}>
                    <div className={classNames(styles.p, styles.p1)}>{strings.p1}</div>
                    <div className={styles.infoContainer}>
                        <div>
                            <span className={styles.label}>{strings.accountNumber}</span>
                            <span>{maskInput(ACCOUNT_NUMBER_MASK, instantCard.accountNumber)}</span>
                        </div>
                        <div>
                            <span className={styles.label}>{strings.expirationDate}</span>
                            <span>{instantCard.expirationDate}</span>
                        </div>
                        <div>
                            <span className={styles.label}>{strings.securityCode}</span>
                            <span>{instantCard.cvv}</span>
                        </div>
                        <div>
                            <span className={styles.label}>{strings.creditLimit}</span>
                            <span>{centsToDollarString(instantCard.creditLimit * 100)}</span>
                        </div>
                    </div>
                    <div className={styles.p}>{strings.p2}</div>
                    <div className={styles.p}>
                        {strings.p3a}
                        <TextLink
                            href={websiteLinkTo}
                            text={strings.website}
                            className={styles.textLink}
                        />
                        {strings.p3b}
                    </div>
                    <div className={styles.p}>{strings.p4}</div>
                </div>
            )}
        </Modal>
    );
}
